import { useGenericCart } from '@contexts/GenericCartContext/GenericCartContext';
import { Product, useGetProductCategoriesMutation, useGetProductsByCategoryMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';

export const useCategoryProductsPageVM = (categoryId: string | undefined) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { items, addItem, removeItem } = useGenericCart({ type: 'appro' });

    const [getProducts, { data, isLoading, isError }] = useGetProductsByCategoryMutation();
    const [categoryTitle, setCategoryTitle] = useState<string | undefined>();
    const [getCategories, { data: categoriesData }] = useGetProductCategoriesMutation();

    const toTitleCase = (phrase: string) =>
        phrase
            .toLowerCase()
            .split(' ')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const handleBasketClick = (product: Product) => {
        setSelectedProduct(product);
        const existingItem = items.find((item) => item.id === product.id);
        setQuantity(existingItem ? existingItem.quantity : 1);
        setShowModal(true);
    };

    const handleValidateQuantity = () => {
        if (selectedProduct) {
            if (quantity === 0) {
                const existingItem = items.find((item) => item.id === selectedProduct.id);
                if (existingItem) {
                    removeItem(selectedProduct.id);
                }
            } else {
                addItem({
                    id: selectedProduct.id,
                    label: selectedProduct.label,
                    quantity,
                    packing_unit: selectedProduct.packing_unit,
                    image: selectedProduct.image
                });
            }
        }
        setShowModal(false);
    };

    const filteredProducts = useMemo(() => {
        if (!searchTerm.trim()) {
            return products;
        }
        return products.filter((product) => product.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [products, searchTerm]);

    useEffect(() => {
        if (categoryId) {
            getProducts(categoryId);
            getCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (data) {
            setProducts(data);
        }

        if (isError) {
            setErrorMessage('Une erreur est survenue lors du chargement des produits');
        }
    }, [data, isLoading, isError]);

    useEffect(() => {
        if (categoriesData) {
            const categoryTitleToFormat = categoriesData.find((category) => category.id === categoryId)?.label;
            if (categoryTitleToFormat) {
                setCategoryTitle(toTitleCase(categoryTitleToFormat));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoriesData]);

    return {
        products,
        isLoading,
        errorMessage,
        toTitleCase,
        categoryTitle,
        showModal,
        setShowModal,
        quantity,
        setQuantity,
        items,
        handleBasketClick,
        handleValidateQuantity,
        searchTerm,
        setSearchTerm,
        filteredProducts
    };
};
